import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import axios from '@/utils/axiosConfig';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { FileText, Plus, Trash2, Check, X, ArrowRight, Send, Clock, Download, Edit } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const Quotes = () => {
  const { user, token } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [formData, setFormData] = useState({
    file_id: '',
    client_id: '',
    description: '',
    tasks_items: [{ description: '', amount: 0, due_date: '' }], // Tasks with amounts and due dates
    debours_items: [{ description: '', amount: 0 }], // Disbursements with amounts
    submit_for_approval: false
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  useEffect(() => {
    // ✅ AbortController pour cleanup automatique
    const abortController = new AbortController();
    let isMounted = true;

    const loadAllData = async () => {
      try {
        const signal = abortController.signal;
        const mountedRef = { current: isMounted };

        await Promise.all([
          fetchQuotes(signal, mountedRef),
          fetchClients(signal, mountedRef),
          fetchFiles(signal, mountedRef)
        ]);
      } catch (error) {
        if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
          console.error('Error loading quotes data:', error);
        }
      }
    };

    loadAllData();

    // ✅ Cleanup: annuler toutes les requêtes lors du démontage
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, []);

  const fetchQuotes = async (signal, isMountedRef = { current: true }) => {
    try {
      const response = await axios.get(`${API}/quotes`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      if (isMountedRef.current) {
        setQuotes(response.data);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        toast.error('Erreur lors du chargement');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchClients = async (signal, isMountedRef = { current: true }) => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      if (isMountedRef.current) {
        setClients(response.data.filter(u => u.role === 'client'));
      }
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error('Failed to fetch clients');
      }
    }
  };

  const fetchFiles = async (signal, isMountedRef = { current: true }) => {
    try {
      const response = await axios.get(`${API}/files`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      if (isMountedRef.current) {
        setFiles(response.data);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error('Failed to fetch files');
      }
    }
  };

  const calculateHonoraires = () => {
    return formData.tasks_items.reduce((sum, task) => sum + (parseFloat(task.amount) || 0), 0);
  };

  const calculateDebours = () => {
    return formData.debours_items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  };

  const calculateTaxes = (honoraires) => {
    const tps = honoraires * 0.05;
    const tvq = honoraires * 0.09975;
    return {
      tps: tps.toFixed(2),
      tvq: tvq.toFixed(2),
      total_taxes: (tps + tvq).toFixed(2),
      subtotal_with_taxes: (honoraires + tps + tvq).toFixed(2)
    };
  };

  const calculateTotal = () => {
    const honoraires = calculateHonoraires();
    const debours = calculateDebours();
    const taxes = calculateTaxes(honoraires);
    return (parseFloat(taxes.subtotal_with_taxes) + debours).toFixed(2);
  };

  const addTask = () => {
    setFormData({
      ...formData,
      tasks_items: [...formData.tasks_items, { description: '', amount: 0, due_date: '' }]
    });
  };

  const removeTask = (index) => {
    const newTasks = formData.tasks_items.filter((_, i) => i !== index);
    setFormData({ ...formData, tasks_items: newTasks });
  };

  const updateTask = (index, field, value) => {
    const newTasks = [...formData.tasks_items];
    if (field === 'amount') {
      newTasks[index][field] = parseFloat(value) || 0;
    } else {
      newTasks[index][field] = value;
    }
    setFormData({ ...formData, tasks_items: newTasks });
  };

  const addDebours = () => {
    setFormData({
      ...formData,
      debours_items: [...formData.debours_items, { description: '', amount: 0 }]
    });
  };

  const removeDebours = (index) => {
    const newDebours = formData.debours_items.filter((_, i) => i !== index);
    setFormData({ ...formData, debours_items: newDebours });
  };

  const updateDebours = (index, field, value) => {
    const newDebours = [...formData.debours_items];
    newDebours[index][field] = field === 'amount' ? parseFloat(value) || 0 : value;
    setFormData({ ...formData, debours_items: newDebours });
  };

  const handleSubmit = async (e, submitForApproval = false) => {
    e.preventDefault();
    
    // Si on édite un devis existant
    if (editingQuote) {
      return handleUpdate(e);
    }
    
    // Sinon, créer un nouveau devis
    try {
      await axios.post(
        `${API}/quotes`,
        {
          file_id: formData.file_id,
          client_id: formData.client_id,
          description: formData.description,
          tasks_items: formData.tasks_items,
          debours_items: formData.debours_items,
          submit_for_approval: submitForApproval
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(submitForApproval ? 'Devis créé et soumis pour approbation' : 'Devis créé avec succès');
      setDialogOpen(false);
      resetForm();
      fetchQuotes();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleSubmitForApproval = async (quoteId) => {
    try {
      await axios.put(
        `${API}/quotes/${quoteId}/submit-for-approval`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Devis soumis pour approbation');
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleApprove = async (quoteId) => {
    try {
      await axios.put(
        `${API}/quotes/${quoteId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Devis approuvé');
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleReject = async (quoteId) => {
    try {
      await axios.put(
        `${API}/quotes/${quoteId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Devis refusé');
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleClientAccept = async (quoteId) => {
    try {
      await axios.put(
        `${API}/quotes/${quoteId}/accept`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Devis accepté');
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleClientReject = async (quoteId) => {
    try {
      await axios.put(
        `${API}/quotes/${quoteId}/client-reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Devis refusé');
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleConvertToInvoice = async (quoteId) => {
    try {
      await axios.put(
        `${API}/quotes/${quoteId}/convert-to-invoice`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Devis converti en facture');
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (quoteId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce devis ?')) {
      return;
    }

    try {
      await axios.delete(
        `${API}/quotes/${quoteId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Devis supprimé');
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({
      file_id: '',
      client_id: '',
      description: '',
      tasks_items: [{ description: '', amount: 0, due_date: '' }],
      debours_items: [{ description: '', amount: 0 }],
      submit_for_approval: false
    });
    setEditingQuote(null);
  };

  const handleEditClick = (quote) => {
    setEditingQuote(quote);
    setFormData({
      file_id: quote.file_id || '',
      client_id: quote.client_id || '',
      description: quote.description || '',
      tasks_items: quote.tasks_items && quote.tasks_items.length > 0 
        ? quote.tasks_items 
        : [{ description: '', amount: 0, due_date: '' }],
      debours_items: quote.debours_items && quote.debours_items.length > 0 
        ? quote.debours_items 
        : [{ description: '', amount: 0 }],
      submit_for_approval: false
    });
    setDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API}/quotes/${editingQuote.id}`,
        {
          file_id: formData.file_id,
          client_id: formData.client_id,
          description: formData.description,
          tasks_items: formData.tasks_items,
          debours_items: formData.debours_items
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Devis modifié avec succès');
      setDialogOpen(false);
      resetForm();
      fetchQuotes();
    } catch (error) {
      toast.error('Erreur lors de la modification');
      console.error('Error updating quote:', error);
    }
  };

  const handleDownloadPDF = async (quoteId) => {
    try {
      const response = await axios.get(
        `${API}/quotes/${quoteId}/pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `devis_${quoteId.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement du PDF');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      brouillon: <Badge className="bg-gray-100 text-gray-800">Brouillon</Badge>,
      en_attente_approbation: <Badge className="bg-yellow-100 text-yellow-800">En attente d'approbation</Badge>,
      approuve: <Badge className="bg-blue-100 text-blue-800">Approuvé</Badge>,
      refuse_admin: <Badge className="bg-red-100 text-red-800">Refusé (Admin)</Badge>,
      accepte_client: <Badge className="bg-green-100 text-green-800">Accepté (Client)</Badge>,
      refuse_client: <Badge className="bg-orange-100 text-orange-800">Refusé (Client)</Badge>,
      converti_facture: <Badge className="bg-purple-100 text-purple-800">Converti en facture</Badge>
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  const getQuoteActions = (quote) => {
    const isAdmin = user?.role === 'admin';
    const isEmployee = user?.role === 'employee';
    const isClient = user?.role === 'client';
    const isOwner = quote.employee_id === user?.id;

    return (
      <div className="flex flex-wrap gap-2 mt-4">
        {/* Edit button for draft and pending approval quotes */}
        {(isAdmin || (isEmployee && isOwner)) && 
         (quote.status === 'brouillon' || quote.status === 'en_attente_approbation') && (
          <Button
            onClick={() => handleEditClick(quote)}
            variant="outline"
            size="sm"
            className="border-blue-600 text-blue-600 hover:bg-blue-50"
          >
            <Edit className="mr-2 h-4 w-4" />
            Modifier
          </Button>
        )}

        {/* Employee actions on draft quotes */}
        {(isEmployee || isAdmin) && isOwner && quote.status === 'brouillon' && (
          <Button
            onClick={() => handleSubmitForApproval(quote.id)}
            className="bg-blue-600 hover:bg-blue-700"
            size="sm"
          >
            <Send className="mr-2 h-4 w-4" />
            Soumettre pour approbation
          </Button>
        )}

        {/* Admin actions on pending quotes */}
        {isAdmin && quote.status === 'en_attente_approbation' && (
          <>
            <Button
              onClick={() => handleApprove(quote.id)}
              className="bg-green-600 hover:bg-green-700"
              size="sm"
            >
              <Check className="mr-2 h-4 w-4" />
              Approuver
            </Button>
            <Button
              onClick={() => handleReject(quote.id)}
              variant="destructive"
              size="sm"
            >
              <X className="mr-2 h-4 w-4" />
              Refuser
            </Button>
          </>
        )}

        {/* Client actions on approved quotes */}
        {isClient && quote.client_id === user?.id && quote.status === 'approuve' && (
          <>
            <Button
              onClick={() => handleClientAccept(quote.id)}
              className="bg-green-600 hover:bg-green-700"
              size="sm"
            >
              <Check className="mr-2 h-4 w-4" />
              Accepter
            </Button>
            <Button
              onClick={() => handleClientReject(quote.id)}
              variant="destructive"
              size="sm"
            >
              <X className="mr-2 h-4 w-4" />
              Refuser
            </Button>
          </>
        )}

        {/* Convert to invoice */}
        {(isAdmin || (isEmployee && isOwner)) && quote.status === 'accepte_client' && (
          <Button
            onClick={() => handleConvertToInvoice(quote.id)}
            className="bg-gradient-to-r from-green-600 to-blue-600"
            size="sm"
          >
            <ArrowRight className="mr-2 h-4 w-4" />
            Convertir en facture
          </Button>
        )}

        {/* Delete button - Admin can delete any, Employee can delete own drafts */}
        {(isAdmin || (isEmployee && isOwner && quote.status === 'brouillon')) && (
          <Button
            onClick={() => handleDelete(quote.id)}
            variant="destructive"
            size="sm"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Supprimer
          </Button>
        )}

        {/* Download PDF button - visible for all users */}
        <Button
          onClick={() => handleDownloadPDF(quote.id)}
          variant="outline"
          className="bg-blue-50"
          size="sm"
        >
          <Download className="mr-2 h-4 w-4" />
          Télécharger PDF
        </Button>
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="quotes-container">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-2">Devis</h1>
            <p className="text-lg text-gray-600">{quotes.length} devis</p>
          </div>

          {(user?.role === 'admin' || user?.role === 'employee') && (
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-red-600 to-blue-600" data-testid="create-quote-btn">
                  <Plus className="mr-2 h-5 w-5" />
                  Créer un devis
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingQuote ? 'Modifier le devis' : 'Nouveau devis'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={(e) => handleSubmit(e, false)} className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Dossier *</Label>
                      <Select value={formData.file_id} onValueChange={(value) => {
                        const selectedFile = files.find(f => f.id === value);
                        if (selectedFile) {
                          setFormData({ ...formData, file_id: value, client_id: selectedFile.client_id });
                        }
                      }}>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner un dossier" />
                        </SelectTrigger>
                        <SelectContent>
                          {files.map((file) => (
                            <SelectItem key={file.id} value={file.id}>
                              {file.file_number} - {file.client_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Description générale</Label>
                      <Input
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Services d'immigration"
                      />
                    </div>
                  </div>

                  {/* SECTION A: HONORAIRES PROFESSIONNELS (TAXÉS) */}
                  <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
                    <h3 className="text-lg font-bold text-blue-900 mb-4">💼 Section A - Honoraires Professionnels (Taxés)</h3>
                    
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <Label className="font-semibold">Liste des tâches avec montants</Label>
                        <Button type="button" onClick={addTask} variant="outline" size="sm">
                          <Plus className="h-4 w-4 mr-2" />
                          Ajouter une tâche
                        </Button>
                      </div>
                      
                      {formData.tasks_items.map((task, index) => (
                        <div key={index} className="bg-white p-3 rounded-lg space-y-2">
                          <div className="grid grid-cols-12 gap-2 items-center">
                            <Input
                              placeholder="Description de la tâche (ex: Préparation du dossier)"
                              value={task.description}
                              onChange={(e) => updateTask(index, 'description', e.target.value)}
                              required
                              className="col-span-6"
                            />
                            <div className="col-span-3 flex items-center gap-2">
                              <span className="text-sm text-gray-600">$</span>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="500.00"
                                value={task.amount}
                                onChange={(e) => updateTask(index, 'amount', e.target.value)}
                                required
                              />
                            </div>
                            <Input
                              type="date"
                              placeholder="Date d'achèvement"
                              value={task.due_date}
                              onChange={(e) => updateTask(index, 'due_date', e.target.value)}
                              className="col-span-2"
                              required
                            />
                            {formData.tasks_items.length > 1 && (
                              <Button
                                type="button"
                                onClick={() => removeTask(index)}
                                variant="ghost"
                                size="sm"
                                className="col-span-1 text-red-600"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 pl-1">📅 Date d'achèvement estimée pour cette tâche</p>
                        </div>
                      ))}
                      
                      <div className="bg-blue-100 p-3 rounded-lg">
                        <div className="flex justify-between items-center font-semibold text-blue-900">
                          <span>Sous-total Honoraires:</span>
                          <span>${calculateHonoraires().toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* SECTION B: DÉBOURS (NON TAXÉS) */}
                  <div className="border-2 border-green-200 rounded-lg p-4 bg-green-50">
                    <h3 className="text-lg font-bold text-green-900 mb-4">📄 Section B - Débours (Non Taxés)</h3>
                    
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <Label className="font-semibold">Liste des débours</Label>
                        <Button type="button" onClick={addDebours} variant="outline" size="sm">
                          <Plus className="h-4 w-4 mr-2" />
                          Ajouter un débours
                        </Button>
                      </div>
                      
                      {formData.debours_items.map((item, index) => (
                        <div key={index} className="grid grid-cols-12 gap-2 items-center bg-white p-3 rounded-lg">
                          <Input
                            placeholder="Description (ex: Frais gouvernementaux)"
                            value={item.description}
                            onChange={(e) => updateDebours(index, 'description', e.target.value)}
                            required
                            className="col-span-7"
                          />
                          <div className="col-span-4 flex items-center gap-2">
                            <span className="text-sm text-gray-600">$</span>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="300.00"
                              value={item.amount}
                              onChange={(e) => updateDebours(index, 'amount', e.target.value)}
                              required
                            />
                          </div>
                          {formData.debours_items.length > 1 && (
                            <Button
                              type="button"
                              onClick={() => removeDebours(index)}
                              variant="ghost"
                              size="sm"
                              className="col-span-1 text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                      
                      <div className="bg-green-100 p-3 rounded-lg">
                        <div className="flex justify-between items-center font-semibold text-green-900">
                          <span>Total Débours (non taxés):</span>
                          <span>${calculateDebours().toFixed(2)}</span>
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-600">Frais gouvernementaux, traductions, etc. (non soumis aux taxes)</p>
                    </div>
                  </div>

                  {/* CALCUL DU TOTAL */}
                  <div className="bg-gradient-to-r from-red-50 to-blue-50 p-6 rounded-lg">
                    <div className="space-y-2">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Honoraires professionnels:</span>
                        <span className="font-semibold">${calculateHonoraires().toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">TPS (5%):</span>
                        <span className="font-semibold">${calculateTaxes(calculateHonoraires()).tps}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">TVQ (9.975%):</span>
                        <span className="font-semibold">${calculateTaxes(calculateHonoraires()).tvq}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm border-t pt-2">
                        <span className="text-gray-600">Sous-total avec taxes:</span>
                        <span className="font-semibold">${calculateTaxes(calculateHonoraires()).subtotal_with_taxes}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Débours (non taxés):</span>
                        <span className="font-semibold">${calculateDebours().toFixed(2)}</span>
                      </div>
                      <div className="border-t-2 pt-2 mt-2 flex justify-between items-center">
                        <span className="text-2xl font-bold">TOTAL À PAYER:</span>
                        <span className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-blue-600">
                          ${calculateTotal()}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      type="submit"
                      variant="outline"
                      className="flex-1"
                      data-testid="save-draft-quote-btn"
                    >
                      Enregistrer comme brouillon
                    </Button>
                    <Button
                      type="button"
                      onClick={(e) => handleSubmit(e, true)}
                      className="flex-1 bg-gradient-to-r from-red-600 to-blue-600"
                      data-testid="submit-quote-btn"
                    >
                      <Send className="mr-2 h-4 w-4" />
                      {user?.role === 'admin' ? 'Créer et approuver' : 'Créer et soumettre'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className="grid grid-cols-1 gap-6">
          {quotes.map((quote) => (
            <Card key={quote.id} className="border-0 shadow-xl" data-testid={`quote-card-${quote.id}`}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-xl mb-2">
                      Devis #{quote.id.substring(0, 8)}
                    </CardTitle>
                    <p className="text-gray-600">{quote.description}</p>
                  </div>
                  <div className="text-right">
                    {getStatusBadge(quote.status)}
                    {/* Display breakdown if honoraires and debours exist */}
                    {quote.honoraires !== undefined && quote.tps_amount !== undefined ? (
                      <div className="space-y-1 text-sm mb-2 mt-2">
                        <div className="text-gray-600">
                          Honoraires: ${quote.honoraires?.toFixed(2)}
                        </div>
                        <div className="text-gray-600">
                          TPS: ${quote.tps_amount?.toFixed(2)} | TVQ: ${quote.tvq_amount?.toFixed(2)}
                        </div>
                        <div className="text-gray-600">
                          Débours: ${quote.debours?.toFixed(2)}
                        </div>
                      </div>
                    ) : null}
                    <p className="text-2xl font-bold mt-2 text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-blue-600">
                      Total: ${quote.amount?.toFixed(2)}
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Créé le:</span>
                      <span className="ml-2 font-medium">{format(new Date(quote.created_at), 'dd/MM/yyyy')}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Valide jusqu'au:</span>
                      <span className="ml-2 font-medium">{format(new Date(quote.valid_until), 'dd/MM/yyyy')}</span>
                    </div>
                  </div>

                  {quote.items && quote.items.length > 0 && (
                    <div className="border-t pt-4">
                      <p className="font-semibold mb-3">Tâches à créer:</p>
                      {quote.items.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm py-2">
                          <Check className="h-4 w-4 text-green-600" />
                          <span>{item.description}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {getQuoteActions(quote)}
                </div>
              </CardContent>
            </Card>
          ))}

          {quotes.length === 0 && (
            <Card className="border-0 shadow-xl">
              <CardContent className="p-12 text-center text-gray-500">
                <FileText className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">Aucun devis</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Quotes;
